import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import {
  Camera,
  MessageSquare,
  Star,
  BarChart3,
  ShieldCheck,
  Sparkles,
  ListChecks,
  Users,
  ChevronDown,
} from 'lucide-react'
import Logo from '@/components/common/Logo'
import { cn } from '@/lib/utils'
import {
  DEFAULT_OG_IMAGE,
  DEFAULT_OG_IMAGE_HEIGHT,
  DEFAULT_OG_IMAGE_WIDTH,
} from '@/lib/seo/siteMeta'

const BENEFITS: Array<{ icon: React.ComponentType<{ className?: string }>; title: string; body: string }> = [
  {
    icon: Camera,
    title: '사진·소개 직접 관리',
    body: '시설 사진과 소개, 프로그램·교통편을 직접 등록하고 노출합니다.',
  },
  {
    icon: MessageSquare,
    title: '보호자 상담 응답',
    body: '입소 상담 요청을 받고 24시간 안에 답변, 알림으로 즉시 확인합니다.',
  },
  {
    icon: Star,
    title: '리뷰 답변 가능',
    body: '등록된 리뷰에 시설 입장에서 답변, 평판을 적극 관리할 수 있습니다.',
  },
  {
    icon: BarChart3,
    title: '방문·문의 통계',
    body: '우리 시설 방문 수, 문의 수, 응답률을 대시보드에서 한눈에 확인합니다.',
  },
]

const DIFFERENTIATORS: Array<{ icon: React.ComponentType<{ className?: string }>; title: string; body: string }> = [
  {
    icon: ShieldCheck,
    title: '공식 데이터 기반',
    body: '건강보험공단·심평원 공식 데이터에 시설 자체 정보를 보강해 신뢰를 만듭니다.',
  },
  {
    icon: Sparkles,
    title: 'AI 맞춤 추천 채널 노출',
    body: '보호자가 부모님 상황을 입력하면 우리 시설이 맞춤 추천 결과에 자동 노출됩니다.',
  },
  {
    icon: ListChecks,
    title: '60항목 시설 선택 체크리스트',
    body: '보호자가 객관적 기준으로 시설을 비교, 우리 시설의 강점이 그대로 드러납니다.',
  },
  {
    icon: Users,
    title: '보호자 커뮤니티 + 인증 후기',
    body: '영수증·입소확인서 기반 인증 후기로 신뢰할 수 있는 평판 환경을 제공합니다.',
  },
]

const PROCESS: Array<{ step: string; title: string; body: string }> = [
  { step: '01', title: '회원가입', body: '이메일·소셜 로그인으로 시설 관리자 계정을 생성합니다.' },
  { step: '02', title: '시설 검색·연결', body: '우리 시설을 찾아 운영자로 연결을 요청합니다.' },
  { step: '03', title: '사업자등록증 업로드', body: '승인을 위해 사업자등록증 사본을 업로드합니다.' },
  { step: '04', title: '승인 후 활성화', body: '영업일 1~2일 안에 승인, 이후 즉시 사진·상담 응답이 가능합니다.' },
]

const FAQS: Array<{ q: string; a: string }> = [
  {
    q: '입점에 비용이 드나요?',
    a: '기본 입점은 무료입니다. 사진·소개·상담 응답·리뷰 답변 모두 무료로 이용하실 수 있습니다. 추가 노출 혜택을 원하시면 로그인 후 [프리미엄] 메뉴에서 확인하실 수 있습니다.',
  },
  {
    q: '어떤 시설이 입점 가능한가요?',
    a: '장기요양기관(요양원·주간보호·방문요양·재가복지센터 등) 및 요양병원·실버타운까지 가능합니다. 국민건강보험공단·심평원 공공데이터에 등록된 시설이면 모두 입점 가능합니다.',
  },
  {
    q: '정보 수정은 누가 하나요?',
    a: '사업자등록증으로 승인된 시설 관리자 계정만 수정 가능합니다. 공공 데이터로 자동 동기화되는 항목(주소·정원·평가등급 등)은 원본을 유지하고, 사진·소개·교통편·프로그램 등 보강 정보만 직접 수정할 수 있습니다.',
  },
  {
    q: '보호자에게 어떻게 노출되나요?',
    a: '검색·지도·AI 맞춤 추천·시설 비교·시설 선택 체크리스트 결과 등 케어밸런스의 모든 보호자 채널에 자동 노출됩니다. 별도 신청이나 추가 비용 없이 자동 반영됩니다.',
  },
  {
    q: '리뷰는 어떻게 관리되나요?',
    a: '시설은 자신의 시설 리뷰에 답변할 수 있으며, 부당한 리뷰는 신고할 수 있습니다. 운영팀이 검수해 허위·악의적 리뷰는 차단합니다. 인증 후기는 영수증·입소확인서 기반으로 검증되어 허위 후기 자체가 노출되지 않습니다.',
  },
  {
    q: '입점 철회는 어떻게 하나요?',
    a: '마이페이지에서 시설 연결 해제 신청을 하거나, 운영팀(contact@carebalance.co.kr)으로 요청하시면 됩니다. 공공 데이터 기반 시설 정보 자체는 그대로 노출되며, 시설관리자 권한과 보강 정보만 제거됩니다.',
  },
  {
    q: '프리미엄은 무엇이 다른가요?',
    a: '사진 추가 등록·검색 결과 상단 배지·방문 통계 등 추가 혜택을 제공합니다. 자세한 내용은 회원가입 및 시설 연결 후 [프리미엄] 메뉴에서 확인하실 수 있습니다.',
  },
  {
    q: '데이터 정확성은 어떻게 보장되나요?',
    a: '공공 데이터는 주기적으로 자동 동기화되며, 시설 자체 입력 정보는 운영자 책임 하에 등록됩니다. 보호자의 신고·운영팀 검수 시스템으로 부정확한 정보를 차단합니다.',
  },
]

function TopNav() {
  return (
    <header className="sticky top-0 z-50 w-full bg-white/90 backdrop-blur border-b border-slate-200">
      <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Logo variant="dark" size="md" />
          <span className="text-sm font-semibold text-slate-500 hidden sm:inline">시설관리자</span>
        </div>
        <nav className="flex items-center gap-2">
          <Link
            to="/facility/login"
            className="px-4 h-10 inline-flex items-center text-sm font-medium text-slate-700 hover:text-slate-900 rounded-lg"
          >
            로그인
          </Link>
          <Link
            to="/facility/signup"
            className="px-4 h-10 inline-flex items-center text-sm font-bold text-white bg-cb-primary hover:opacity-90 rounded-lg transition"
          >
            무료 입점
          </Link>
        </nav>
      </div>
    </header>
  )
}

function Hero() {
  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-slate-50 to-blue-50">
      <div className="max-w-6xl mx-auto px-6 py-20 sm:py-28">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-100 text-cb-primary text-xs font-bold mb-6">
              <Sparkles className="w-3.5 h-3.5" />
              <span>시설 운영자 전용</span>
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 leading-tight">
              부모님 시설을 찾는<br />
              보호자에게 우리 시설을<br />
              직접 보여주세요
            </h1>
            <p className="mt-6 text-base sm:text-lg text-slate-600 leading-relaxed">
              건강보험공단·심평원 공식 데이터에 시설 운영자가 직접 사진·소개·상담 응답을 더해, 보호자 신뢰를 만듭니다.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link
                to="/facility/signup"
                className="inline-flex items-center justify-center px-6 h-12 text-base font-bold text-white bg-cb-primary hover:opacity-90 rounded-xl shadow-sm transition"
              >
                무료로 입점 신청하기
              </Link>
              <Link
                to="/facility/login"
                className="inline-flex items-center justify-center px-6 h-12 text-base font-semibold text-slate-700 bg-white hover:bg-slate-50 border border-slate-300 rounded-xl transition"
              >
                관리자 로그인
              </Link>
            </div>
          </div>
          <div className="hidden lg:block">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-tr from-blue-200/40 to-transparent rounded-3xl blur-2xl" />
              <div className="relative bg-white rounded-3xl shadow-2xl border border-slate-200 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-cb-primary/10 flex items-center justify-center">
                    <BarChart3 className="w-5 h-5 text-cb-primary" />
                  </div>
                  <div>
                    <div className="text-xs text-slate-500">시설관리자 대시보드</div>
                    <div className="text-sm font-bold text-slate-900">○○요양원</div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div className="rounded-xl bg-slate-50 p-3">
                    <div className="text-xs text-slate-500">이번달 문의</div>
                    <div className="text-lg font-bold text-slate-900">24건</div>
                  </div>
                  <div className="rounded-xl bg-slate-50 p-3">
                    <div className="text-xs text-slate-500">응답률</div>
                    <div className="text-lg font-bold text-cb-primary">96%</div>
                  </div>
                  <div className="rounded-xl bg-slate-50 p-3">
                    <div className="text-xs text-slate-500">관심 등록</div>
                    <div className="text-lg font-bold text-slate-900">182명</div>
                  </div>
                  <div className="rounded-xl bg-slate-50 p-3">
                    <div className="text-xs text-slate-500">평균 별점</div>
                    <div className="text-lg font-bold text-amber-500">4.6</div>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-xs">
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                    <span className="text-slate-600">새 상담 요청 · 5분 전</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                    <span className="text-slate-600">리뷰 1건 등록 · 32분 전</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

function Benefits() {
  return (
    <section className="bg-white py-20 sm:py-24">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900">
            무료 입점만으로도 받는 혜택
          </h2>
          <p className="mt-3 text-base text-slate-600">
            가입 즉시 사용 가능한 시설 운영자 기본 기능입니다.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {BENEFITS.map((b) => (
            <div key={b.title} className="rounded-2xl bg-slate-50 p-6 hover:bg-slate-100 transition">
              <div className="w-12 h-12 rounded-xl bg-white shadow-sm flex items-center justify-center mb-4">
                <b.icon className="w-6 h-6 text-cb-primary" />
              </div>
              <h3 className="text-base font-bold text-slate-900 mb-2">{b.title}</h3>
              <p className="text-sm text-slate-600 leading-relaxed">{b.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

function Differentiators() {
  return (
    <section className="bg-slate-50 py-20 sm:py-24">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900">
            왜 케어밸런스인가
          </h2>
          <p className="mt-3 text-base text-slate-600">
            보호자가 신뢰할 수 있는 환경에서 우리 시설이 정확하게 노출됩니다.
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-5">
          {DIFFERENTIATORS.map((d, idx) => (
            <div key={d.title} className="rounded-2xl bg-white border border-slate-200 p-6">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-xl bg-cb-primary/10 flex items-center justify-center flex-shrink-0">
                  <d.icon className="w-5 h-5 text-cb-primary" />
                </div>
                <div>
                  <div className="text-xs font-bold text-cb-primary mb-1">0{idx + 1}</div>
                  <h3 className="text-base font-bold text-slate-900 mb-2">{d.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{d.body}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

function Process() {
  return (
    <section className="bg-white py-20 sm:py-24">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900">
            4단계로 입점 완료
          </h2>
          <p className="mt-3 text-base text-slate-600">
            영업일 1~2일 안에 시설관리자 권한이 활성화됩니다.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {PROCESS.map((p) => (
            <div key={p.step} className="rounded-2xl bg-slate-50 p-6 border border-slate-100">
              <div className="text-xs font-bold text-cb-primary mb-2">STEP {p.step}</div>
              <h3 className="text-base font-bold text-slate-900 mb-2">{p.title}</h3>
              <p className="text-sm text-slate-600 leading-relaxed">{p.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

function FaqItem({ q, a, open, onToggle }: { q: string; a: string; open: boolean; onToggle: () => void }) {
  return (
    <div className="border-b border-slate-200">
      <button
        type="button"
        onClick={onToggle}
        className="w-full text-left flex items-center justify-between py-5 gap-4 hover:text-cb-primary transition"
        aria-expanded={open}
      >
        <span className="text-base font-semibold text-slate-900">{q}</span>
        <ChevronDown className={cn('w-5 h-5 text-slate-500 transition-transform flex-shrink-0', open && 'rotate-180 text-cb-primary')} />
      </button>
      {open && (
        <div className="pb-5 text-sm text-slate-600 leading-relaxed whitespace-pre-line">{a}</div>
      )}
    </div>
  )
}

function Faq() {
  const [openIdx, setOpenIdx] = useState<number | null>(0)
  return (
    <section className="bg-slate-50 py-20 sm:py-24">
      <div className="max-w-3xl mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900">
            자주 묻는 질문
          </h2>
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 px-6 sm:px-8">
          {FAQS.map((f, idx) => (
            <FaqItem
              key={f.q}
              q={f.q}
              a={f.a}
              open={openIdx === idx}
              onToggle={() => setOpenIdx(openIdx === idx ? null : idx)}
            />
          ))}
        </div>
      </div>
    </section>
  )
}

function BottomCta() {
  return (
    <section className="bg-cb-primary text-white py-16 sm:py-20">
      <div className="max-w-3xl mx-auto px-6 text-center">
        <h2 className="text-2xl sm:text-3xl font-extrabold leading-tight">
          지금 무료로 시설을 등록해 보세요
        </h2>
        <p className="mt-4 text-sm sm:text-base text-blue-100">
          영업일 1~2일 안에 승인됩니다. 지금 시작하면 이번 주 안에 활성화됩니다.
        </p>
        <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
          <Link
            to="/facility/signup"
            className="inline-flex items-center justify-center px-6 h-12 text-base font-bold text-cb-primary bg-white hover:bg-slate-50 rounded-xl shadow-sm transition"
          >
            무료로 입점 신청하기
          </Link>
          <a
            href="mailto:contact@carebalance.co.kr"
            className="inline-flex items-center justify-center px-6 h-12 text-base font-semibold text-white border border-white/40 hover:bg-white/10 rounded-xl transition"
          >
            상담 문의
          </a>
        </div>
      </div>
    </section>
  )
}

function MiniFooter() {
  return (
    <footer className="bg-slate-900 text-slate-400">
      <div className="max-w-6xl mx-auto px-6 py-10">
        <div className="mb-3">
          <Logo variant="light" size="sm" />
        </div>
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs leading-relaxed">
          <span>상호: 파인드플레이스</span>
          <span className="text-slate-700">|</span>
          <span>대표: 우상철</span>
          <span className="text-slate-700">|</span>
          <span>사업자등록번호: 587-18-02704</span>
          <span className="text-slate-700">|</span>
          <span>이메일: contact@carebalance.co.kr</span>
        </div>
        <div className="mt-1 text-xs">
          주소: 경기도 의정부시 서부로 545 경민대학교 창업관 7층 754-1호 파인드플레이스
        </div>
        <div className="mt-6 text-xs text-slate-500">
          &copy; 2026 파인드플레이스. All rights reserved.
        </div>
      </div>
    </footer>
  )
}

export default function BizIntroPage() {
  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Helmet>
        <title>시설 입점 안내 — 케어밸런스 시설관리자</title>
        <meta
          name="description"
          content="요양시설 무료 입점, 보호자 상담 응답·사진 등록·리뷰 답변까지. 건강보험공단·심평원 공식 데이터 기반 케어밸런스에서 우리 시설을 직접 알리세요."
        />
        <meta property="og:title" content="시설 입점 안내 — 케어밸런스 시설관리자" />
        <meta
          property="og:description"
          content="요양시설 무료 입점, 보호자 상담 응답·사진 등록·리뷰 답변까지. 건강보험공단·심평원 공식 데이터 기반 케어밸런스에서 우리 시설을 직접 알리세요."
        />
        <meta property="og:url" content="https://biz.carebalance.co.kr/" />
        {/* 공유 미리보기 — 기본 og 이미지와 실측 크기 (ssr-spec §17.3.5, §17.4.3) */}
        <meta property="og:type" content="website" />
        <meta property="og:image" content={DEFAULT_OG_IMAGE} />
        <meta property="og:image:width" content={String(DEFAULT_OG_IMAGE_WIDTH)} />
        <meta property="og:image:height" content={String(DEFAULT_OG_IMAGE_HEIGHT)} />
        <meta property="og:site_name" content="케어밸런스(CareBalance)" />
        <link rel="canonical" href="https://biz.carebalance.co.kr/" />
      </Helmet>
      <TopNav />
      <main className="flex-1">
        <Hero />
        <Benefits />
        <Differentiators />
        <Process />
        <Faq />
        <BottomCta />
      </main>
      <MiniFooter />
    </div>
  )
}
